import React from "react";
import {
  Panel,
  PanelType,
  IconButton,
  Text,
  Link,
  Icon,
  Stack,
  mergeStyleSets,
  useTheme,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNotifications, NotificationSeverity, INotification } from "../../contexts/NotificationContext";

dayjs.extend(relativeTime);

interface NotificationPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const getStyles = (theme: any) =>
  mergeStyleSets({
    panelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 24px",
      borderBottom: `1px solid ${theme.palette.neutralLight}`,
      boxSizing: "border-box",
      width: "100%",
      maxWidth: "380px",
    },
    panelTitle: {
      fontSize: 20,
      fontWeight: 600,
      color: theme.palette.neutralPrimary,
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "60px 24px",
      textAlign: "center",
    },
    bellIcon: {
      fontSize: 64,
      color: theme.palette.neutralTertiary,
      marginBottom: 16,
    },
    emptyText: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.neutralPrimary,
      marginBottom: 8,
    },
    notificationItem: {
      padding: "12px 24px",
      borderBottom: `1px solid ${theme.palette.neutralLighter}`,
      cursor: "pointer",
      boxSizing: "border-box",
      maxWidth: "100%",
      overflow: "hidden",
      ":hover": {
        backgroundColor: theme.palette.neutralLighterAlt,
      },
    },
    unreadItem: {
      borderLeft: `3px solid ${theme.palette.themePrimary}`,
      paddingLeft: "21px",
    },
    notificationTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.neutralPrimary,
    },
    notificationMessage: {
      fontSize: 12,
      color: theme.palette.neutralSecondary,
      marginTop: 4,
      wordBreak: "break-word",
      overflowWrap: "break-word",
    },
    notificationTime: {
      fontSize: 11,
      color: theme.palette.neutralTertiary,
      marginTop: 4,
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      padding: "8px 24px",
      borderBottom: `1px solid ${theme.palette.neutralLight}`,
      gap: 8,
    },
  });

const severityToMessageBarType: Record<NotificationSeverity, MessageBarType> = {
  [NotificationSeverity.Info]: MessageBarType.info,
  [NotificationSeverity.Success]: MessageBarType.success,
  [NotificationSeverity.Warning]: MessageBarType.warning,
  [NotificationSeverity.Error]: MessageBarType.error,
};

const NotificationItem: React.FC<{
  notification: INotification;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
  styles: ReturnType<typeof getStyles>;
}> = ({ notification, onRead, onDismiss, styles }) => {
  const handleClick = () => {
    if (!notification.isRead) {
      onRead(notification.id);
    }
  };

  return (
    <div
      className={`${styles.notificationItem} ${!notification.isRead ? styles.unreadItem : ""}`}
      onClick={handleClick}
    >
      <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
        <Stack.Item grow>
          <MessageBar
            messageBarType={severityToMessageBarType[notification.severity]}
            isMultiline={false}
            styles={{ root: { background: "transparent", padding: 0, border: "none" } }}
          >
            <Text className={styles.notificationTitle}>{notification.title}</Text>
          </MessageBar>
          <Text className={styles.notificationMessage} block>
            {notification.message}
          </Text>
          {notification.link && (
            <Link
              href={notification.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {notification.linkText || "View details"}
            </Link>
          )}
          <Text className={styles.notificationTime} block>
            {dayjs(notification.timestamp).fromNow()}
          </Text>
        </Stack.Item>
        <Stack.Item>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            title="Dismiss"
            ariaLabel="Dismiss notification"
            onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
            onKeyDown={(e) => e.stopPropagation()}
            styles={{ root: { width: 24, height: 24 }, icon: { fontSize: 12 } }}
          />
        </Stack.Item>
      </Stack>
    </div>
  );
};

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onDismiss }) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { notifications, markAsRead, markAllAsRead, dismissNotification, clearAll } = useNotifications();

  const onRenderHeader = (): JSX.Element => (
    <div className={styles.panelHeader} style={{ width: "100%" }}>
      <Text className={styles.panelTitle}>Notifications</Text>
      <IconButton
        iconProps={{ iconName: "Cancel" }}
        ariaLabel="Close notifications"
        onClick={onDismiss}
        styles={{ root: { width: 32, height: 32 } }}
      />
    </div>
  );

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.custom}
      customWidth="380px"
      isLightDismiss
      hasCloseButton={false}
      onRenderHeader={() => onRenderHeader()}
      styles={{
        content: { padding: 0, overflowX: "hidden" },
        scrollableContent: { overflowX: "hidden", overflowY: "auto" },
        main: { overflowX: "hidden" },
      }}
    >
      {notifications.length > 0 && (
        <div className={styles.toolbar}>
          <Link onClick={markAllAsRead} style={{ fontSize: 12 }}>
            Mark all as read
          </Link>
          <Text style={{ color: theme.palette.neutralTertiary }}>|</Text>
          <Link onClick={clearAll} style={{ fontSize: 12 }}>
            Clear all
          </Link>
        </div>
      )}

      {notifications.length === 0 ? (
        <div className={styles.emptyState}>
          <Icon iconName="Ringer" className={styles.bellIcon} />
          <Text className={styles.emptyText}>No new notifications from this session</Text>
        </div>
      ) : (
        <div>
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={markAsRead}
              onDismiss={dismissNotification}
              styles={styles}
            />
          ))}
        </div>
      )}
    </Panel>
  );
};

export default NotificationPanel;
